import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ConfirmModal } from '../components/ui/ConfirmModal';

type ConfirmVariant = 'danger' | 'warning' | 'neutral';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  required?: boolean;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{
    isOpen: boolean;
    isPrompt: boolean;
    resolve: ((value: any) => void) | null;
    options: ConfirmOptions & PromptOptions;
  }>({
    isOpen: false,
    isPrompt: false,
    resolve: null,
    options: { title: '', message: '' },
  });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        isPrompt: false,
        resolve,
        options,
      });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        isPrompt: true,
        resolve,
        options,
      });
    });
  }, []);

  const handleConfirm = useCallback((value?: string) => {
    if (state.isPrompt) {
      state.resolve?.(value || '');
    } else {
      state.resolve?.(true);
    }
    setState((s) => ({ ...s, isOpen: false, resolve: null }));
  }, [state]);

  const handleCancel = useCallback(() => {
    if (state.isPrompt) {
      state.resolve?.(null);
    } else {
      state.resolve?.(false);
    }
    setState((s) => ({ ...s, isOpen: false, resolve: null }));
  }, [state]);

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <ConfirmModal
        isOpen={state.isOpen}
        isPrompt={state.isPrompt}
        title={state.options.title}
        message={state.options.message}
        placeholder={state.options.placeholder}
        required={state.options.required}
        confirmLabel={state.options.confirmLabel}
        cancelLabel={state.options.cancelLabel}
        variant={state.options.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};
